import { ShopOutlined } from '@ant-design/icons';
import {
  IssueCase,
  IssueCaseCategory,
  IssueCaseCreatorType,
  IssueCaseKind,
  IssueCaseRequestCategory,
  IssueSourceType,
} from '@gooduncles/gu-app-schema';
import { Button, Form, Input, Segmented, Select, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';

import { issueCaseCategoryKr } from 'src/constants/issue.constants';
import { createIssueCase } from 'src/utils/firebase/firebase-short-cut';
import { getIssueCaseInitialData, issueCaseIssuedByOptions, issueCaseSubOptions } from 'src/utils/issue/issue-util';

import classes from './CreateFormSection.module.scss';

import { useCrmBoardContext } from '../../CrmBoardContext';
import LinkedIssueCaseBadge from './LinkedIssueCaseBadge';
import LinkedOrderBadge from './LinkedOrderBadge';

type AnyCategory = IssueCaseCategory | IssueCaseRequestCategory;

interface FormValues {
  category: AnyCategory;
  subcategory?: string;
  issuedBy: string;
  content?: string;
  publicNote: string;
  privateNote?: string;
}

const ISSUE_CATEGORIES = new Set<string>(Object.values(IssueCaseCategory));
const REQUEST_CATEGORIES = new Set<string>(Object.values(IssueCaseRequestCategory));

const caseKindOptions: { label: string; value: IssueCaseKind }[] = [
  { label: '이슈', value: 'issue' },
  { label: '요청', value: 'request' },
];

const CreateFormSection: FC = () => {
  const { user } = useAuth();
  const [form] = Form.useForm<FormValues>();
  const { selectedStore, linkedOrder, setLinkedOrder, linkedIssueCase, setLinkedIssueCase } = useCrmBoardContext();

  const [caseKind, setCaseKind] = useState<IssueCaseKind>('issue');
  const selectedCategory = Form.useWatch('category', form);

  const categoryOptions = useMemo(() => {
    const validKeys = caseKind === 'issue' ? ISSUE_CATEGORIES : REQUEST_CATEGORIES;
    return Object.entries(issueCaseCategoryKr)
      .filter(([key]) => validKeys.has(key))
      .map(([value, label]) => ({ label, value }));
  }, [caseKind]);

  const subcategoryOptions = useMemo(() => {
    if (!selectedCategory) return [];
    return issueCaseSubOptions(selectedCategory) ?? [];
  }, [selectedCategory]);

  useEffect(() => {
    form.setFieldsValue({ category: undefined, subcategory: undefined });
  }, [caseKind, form]);

  useEffect(() => {
    form.setFieldValue('subcategory', undefined);
  }, [selectedCategory, form]);

  const handleSubmit = async () => {
    if (!selectedStore || !user) {
      message.error('매장을 먼저 선택해주세요.');
      return;
    }

    try {
      const values = await form.validateFields();

      const source = (() => {
        if (linkedOrder) {
          return {
            sourceType: IssueSourceType.ORDER,
            sourceId: linkedOrder._id,
            sourceReferenceDate: linkedOrder.deliveryDate ?? null,
          };
        }
        if (linkedIssueCase) {
          return {
            sourceType: IssueSourceType.ISSUE,
            sourceId: linkedIssueCase._id,
            sourceReferenceDate: linkedIssueCase.registeredAt ?? null,
          };
        }
        return { sourceType: null, sourceId: null, sourceReferenceDate: null };
      })();

      const base = getIssueCaseInitialData({
        ...source,
        storeId: selectedStore.id,
        storeName: selectedStore.name,
        creatorId: user._id,
        creatorType: user.role as IssueCaseCreatorType,
        category: values.category,
        subcategory: (values.subcategory as any) ?? null,
        caseKind,
        content: caseKind === 'request' ? values.content ?? null : null,
      });

      const issueData: IssueCase = {
        ...base,
        issuedBy: values.issuedBy as any,
        publicNote: values.publicNote,
        privateNote: values.privateNote ?? null,
      };
      await createIssueCase(issueData);
      message.success(caseKind === 'request' ? '요청이 등록되었습니다.' : '이슈가 등록되었습니다.');

      form.resetFields();
      setLinkedOrder(null);
      setLinkedIssueCase(null);
    } catch (error) {
      console.error('Failed to create:', error);
      message.error('등록에 실패했습니다.');
    }
  };

  if (!selectedStore) {
    return (
      <div className={`${classes.container} ${classes.verticalContainer}`}>
        <div className={classes.emptyState}>
          <ShopOutlined className={classes.emptyIcon} />
          <div>매장을 선택하면 이슈/요청을 등록할 수 있습니다</div>
        </div>
      </div>
    );
  }

  const formLabelCol = { flex: '0 0 70px' };
  const formWrapperCol = { flex: 1 };

  return (
    <div className={`${classes.container} ${classes.verticalContainer}`}>
      <Form form={form} labelCol={formLabelCol} wrapperCol={formWrapperCol} labelAlign='right'>
        <div className={classes.linkedOrderRow}>
          {linkedOrder ? (
            <LinkedOrderBadge order={linkedOrder} onRemove={() => setLinkedOrder(null)} />
          ) : linkedIssueCase ? (
            <LinkedIssueCaseBadge issueCase={linkedIssueCase} onRemove={() => setLinkedIssueCase(null)} />
          ) : (
            <span className={classes.noLinkedOrder}>연결된 주문/이슈 없음</span>
          )}
        </div>

        <Form.Item label='유형'>
          <Segmented
            options={caseKindOptions}
            value={caseKind}
            onChange={(val) => setCaseKind(val as IssueCaseKind)}
            block
          />
        </Form.Item>

        <Form.Item name='issuedBy' label='출처' rules={[{ required: true, message: '필수' }]} initialValue='customer'>
          <Select options={issueCaseIssuedByOptions} placeholder='선택' />
        </Form.Item>

        <Form.Item name='category' label='분류' rules={[{ required: true, message: '필수' }]}>
          <Select options={categoryOptions} placeholder='선택' />
        </Form.Item>

        <Form.Item name='subcategory' label='상세분류'>
          <Select
            options={subcategoryOptions}
            placeholder='선택'
            disabled={!selectedCategory || subcategoryOptions.length === 0}
            allowClear
          />
        </Form.Item>

        {caseKind === 'request' && (
          <Form.Item name='content' label='요청 내용'>
            <Input.TextArea placeholder='내용 입력' rows={2} />
          </Form.Item>
        )}

        <Form.Item name='privateNote' label='내부 메모' rules={[{ required: true, message: '필수' }]}>
          <Input.TextArea placeholder='내용 입력' rows={2} />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 0, flex: 1 }} className={classes.buttonFormItem}>
          <Button type='primary' block onClick={handleSubmit}>
            등록
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CreateFormSection;
